import { useState, useRef, useEffect } from "react";
import { useI18n, LANGUAGES } from "../lib/i18n";
import api from "../lib/api";

const MENU_WIDTH = 176; // w-44
const VIEWPORT_MARGIN = 8;

export function LanguageSwitcher({ persistEndpoint = null, className = "" }) {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[data-testid="language-switcher-menu"]')) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const compute = () => {
      const rect = btnRef.current.getBoundingClientRect();
      // Anchor to the button's right margin by default, but clamp so it never spills off-screen.
      let left = rect.right - MENU_WIDTH;
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN));
      setPos({ top: rect.bottom + 8, left });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  const pick = async (code) => {
    setLang(code);
    setOpen(false);
    if (persistEndpoint) {
      try { await api.put(persistEndpoint, { language: code }); } catch {}
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className="tf-btn tf-btn-ghost !px-3 text-sm font-display font-bold"
        data-testid="language-switcher-trigger"
      >
        <span className="text-base">{current.flag}</span>
        <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
      </button>
      {open && (
        <div
          className="fixed w-44 bg-white border border-[var(--tf-border)] rounded-2xl shadow-md py-1.5 z-[100]"
          style={{ top: pos.top, left: pos.left }}
          data-testid="language-switcher-menu"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left hover:bg-[var(--tf-bg)] transition-colors ${
                l.code === lang ? "font-bold text-[var(--tf-ink)]" : "text-[var(--tf-muted)]"
              }`}
            >
              <span className="text-base">{l.flag}</span> {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
